import { PUBLICATION_STATUSES, type PaginationQuery } from '@diora/contracts';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Select } from '@/components/ui';

/**
 * Search and status, shared by every list screen.
 *
 * The search box is debounced rather than fired per keystroke: the API matches
 * both languages case-insensitively, which is not a free query, and a request
 * per character would put half-typed words in front of the person typing them.
 */
export function ListFilters({
  params,
  onChange,
  children,
}: {
  params: PaginationQuery;
  onChange: (next: Partial<PaginationQuery>) => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState(params.search ?? '');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((params.search ?? '') !== search) onChange({ search: search || undefined });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, params.search, onChange]);

  return (
    <>
      {/* The width is the toolbar's business, not this component's — see
          `.dio-toolbar .dio-input-group`, which a media query can reach. */}
      <div className="dio-input-group">
        <span className="dio-input-group__addon" aria-hidden="true">
          <Search className="dio-icon" />
        </span>
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common.searchPlaceholder')}
          aria-label={t('common.search')}
        />
      </div>

      <Select
        value={params.status ?? ''}
        aria-label={t('status.label')}
        onChange={(event) =>
          onChange({ status: (event.target.value || undefined) as PaginationQuery['status'] })
        }
      >
        <option value="">{t('common.all')}</option>
        {PUBLICATION_STATUSES.map((status) => (
          <option key={status} value={status}>
            {t(`status.${status}`)}
          </option>
        ))}
      </Select>

      {children}
    </>
  );
}
